import { useState, useMemo } from "react";
import {
  DndContext,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import ConfirmModal from "components/common/ConfirmModal";
import ImageViewer from "components/common/ImageViewer";
import { MdEdit, MdDelete } from "react-icons/md";
import { getImageUrl } from "utils/imageHelper";
import { useBanners } from "components/banners/hooks/useBanners";



function SortableRow({ banner, onEdit, onAskDelete, openViewer }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: banner._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isVideoFile = (path = "") =>
  /\.(mp4|webm|ogg)$/i.test(path);

    

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="hover:bg-gray-50 transition"
    >
      <td
        {...attributes}
        {...listeners}
        className="px-4 cursor-move text-gray-400 text-lg"
        title="Drag to reorder"
        onClick={(e) => e.stopPropagation()}
      >
        ⠿
      </td>

      <td className="px-6 py-4">
       {banner.media_type === "video" && banner.banner_video ? (
        <video
          src={getImageUrl(banner.banner_video)}
          className="h-12 w-20 rounded-lg border object-cover cursor-pointer"
          muted
          playsInline
          onClick={(e) => {
          e.stopPropagation();
          openViewer({
            url: banner.banner_video,
            type: "video",
          });
        }}
        />
      ) : banner.banner_image ? (
        <img
          src={getImageUrl(banner.banner_image)}
          alt={banner.title}
          className="h-12 w-20 rounded-lg object-cover border cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            openViewer({
              url: banner.banner_image,
              type: "image",
            });
          }}

        />
      ) : (
        <div className="h-12 w-20 rounded-lg bg-gray-100 flex items-center justify-center text-xs text-gray-400">
          no media
        </div>
      )}

      </td>

      <td className="px-6 py-4 font-medium text-gray-800">
        {banner.title}
      </td>

      <td className="px-6 py-4">
        <span
          className={`rounded-full px-3 py-1 text-xs font-semibold ${
            banner.status === "live"
              ? "bg-green-100 text-green-700"
              : "bg-gray-200 text-gray-600"
          }`}
        >
          {banner.status}
        </span>
      </td>

      <td className="px-6 py-4">
        <div className="flex justify-end gap-3">
          <button
            onClick={() => onEdit(banner)}
            className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 hover:text-blue-600"
            title="Edit"
          >
            <MdEdit size={18} />
          </button>

          <button
            onClick={() => onAskDelete(banner._id)}
            className="rounded-lg p-2 text-gray-600 hover:bg-red-50 hover:text-red-600"
            title="Delete"
          >
            <MdDelete size={18} />
          </button>
        </div>
      </td>
    </tr>
  );
}


export default function BannerTable({
  bannerType,
  data = [],
  loading,
  onEdit,
  onDelete,
  onReorder,
}) {
  const banners = Array.isArray(data?.data) ? data.data : [];

  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerSrc, setViewerSrc] = useState(null);
  const [viewerData, setViewerData] = useState(null);
  

  const sortedBanners = useMemo(
    () =>
      [...banners].sort(
        (a, b) => (a.priority_order || 0) - (b.priority_order || 0)
      ),
    [banners]
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedBanners.findIndex(
      (b) => b._id === active.id
    );
    const newIndex = sortedBanners.findIndex(
      (b) => b._id === over.id
    );

    const reordered = arrayMove(
      sortedBanners,
      oldIndex,
      newIndex
    );

    const payload = reordered.map((b, index) => ({
      _id: b._id,
      priority_order: index + 1,
    }));

    await onReorder(payload);
  };

  const confirmDelete = async () => {
    try {
      setDeleting(true);
      await onDelete(deleteId);
      setDeleteId(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="space-y-6">
        <h2 className="text-xl font-semibold">
          {bannerType} Banners
        </h2>

        <div className="overflow-hidden rounded-xl bg-white shadow-sm">
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortedBanners.map((b) => b._id)}
              strategy={verticalListSortingStrategy}
            >
              <table className="w-full text-left">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-4"></th>
                    <th className="px-6 py-4 text-sm font-semibold text-gray-600">
                      Image
                    </th>
                    <th className="px-6 py-4 text-sm font-semibold text-gray-600">
                      Banner Title
                    </th>
                    <th className="px-6 py-4 text-sm font-semibold text-gray-600">
                      Status
                    </th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y">
                  {loading && (
                    <tr>
                      <td
                        colSpan="5"
                        className="px-6 py-10 text-center text-sm text-gray-500"
                      >
                        Loading...
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    sortedBanners.map((b) => (
                      <SortableRow
                        key={b._id}
                        banner={b}
                        onEdit={onEdit}
                        onAskDelete={setDeleteId}
                        openViewer={(data) => {
                          setViewerData({
                            url: getImageUrl(data.url),
                            type: data.type,
                          });
                          setViewerOpen(true);
                        }}

                      />
                    ))}

                  {!loading && !sortedBanners.length && (
                    <tr>
                      <td
                        colSpan="5"
                        className="px-6 py-10 text-center text-sm text-gray-500"
                      >
                        No banners found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </SortableContext>
          </DndContext>
        </div>
      </div>

      <ConfirmModal
        open={Boolean(deleteId)}
        title="Delete Banner"
        message="This action cannot be undone. Do you want to continue?"
        confirmText="Delete"
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />

      <ImageViewer
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        src={viewerData?.url}
        type={viewerData?.type}
      >
      </ImageViewer>

    </>
  );
}
